import {plural, t} from "@lingui/core/macro";

import type {ApolloGqlClient} from "@repo/data-gql-apollo";
import Log, {Errors} from "@repo/logging";

import {
    checkUsernameQuery,
    type CheckUsernameError,
} from "./check-username-query.ts";

export const PASSWORD_MIN_LENGTH = 8;

// For most users, after login they'll be redirected to their role's homepage
// (eg. `/` for regular students). There are some special paths that
// require alternate redirections after login, though. This array collects the
// URLs that should override default redirect behavior.
const FORCE_CONTINUE_URLS = ["/coaches"];

// The typical flow is for users who sign up successfully to be
// redirected to the homepages of their user roles. However, we might
// wish to override this behavior if we know users are coming to the
// signup form from special endpoints or if they have alwayscontinue
// set as a parameter. In these cases, wesend them to the continue URL
// specified by their query params when they successfully sign up.
export const getContinueUrlFromQueryString = (): null | string => {
    const params = new URLSearchParams(
        // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
        // eslint-disable-next-line no-restricted-syntax -- see above
        window.location.search,
    );
    const continueUrl = params.get("continue") || "";
    // If continue URL from window.location has the protocol or hostname or
    // both, replace them so that only the relative path remains.
    const continuePath = continueUrl
        .replace(location.protocol + "//", "") // protocol is like "https:"
        .replace(location.host, "");

    // NOTE: We cannot just let the continue parameter fallback to the
    // empty string "" because the form will not allow any parameters
    // with an empty value to be POSTed leading to a broken sign up form!
    if (
        FORCE_CONTINUE_URLS.includes(continuePath) ||
        params.get("alwayscontinue")
    ) {
        return continuePath;
    }
    return null;
};

// This is a simple (and non-exhaustive) test that an email has valid
// format. Emails should begin with 1 or more characters, followed by "@",
// and then 3 or more word characters where at least one "." appears in the
// middle.
// From https://emailregex.com/
export const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const isEmailValid = (email: string): string | boolean =>
    email && EMAIL_REGEX.test(email);

// sync-start:nickname-length 87023242 https://github.com/Khan/webapp/blob/master/services/users/user_data/nickname.go
export const NICKNAME_MAX_LENGTH = 40;
// sync-end:nickname-length

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 40;
const USERNAME_VALIDATION_STRINGS = {
    GENERIC_ERROR: () =>
        plural(USERNAME_MAX_LENGTH, {
            one: `Usernames must be alphanumeric, start with a letter, and be ${USERNAME_MIN_LENGTH} to # character long`,
            other: `Usernames must be alphanumeric, start with a letter, and be ${USERNAME_MIN_LENGTH} to # characters long`,
        }) as string,
} as const;

export const getUsernameValidationString = (
    errorCode: CheckUsernameError,
): string => {
    switch (errorCode) {
        case "BAD_CHARACTERS":
            return USERNAME_VALIDATION_STRINGS.GENERIC_ERROR();

        case "INTERNAL":
            return t`Something went wrong.`;

        case "TOO_SHORT":
            return t`Too short.`;

        case "UNAVAILABLE":
            return t`This username is unavailable.`;

        default:
            const _: never = errorCode;

            // We would end up here if there is an error code that's not
            // supported is passed into this function.
            Log.error(
                `CheckUsernameError code not supported: ${errorCode}`,
                Errors.Internal,
            );
            return USERNAME_VALIDATION_STRINGS.GENERIC_ERROR();
    }
};

/**
 * Performs a check to see whether the username is valid.
 */
export const isUsernameValid = (
    username: string,
): {
    result: boolean;
    reason: string;
} => {
    // NOTE: The client-side validations in this method must be share the same
    // understanding of a valid username as the server. As of ADR #283, we
    // also include a client-side 40 character length constraint and we expect
    // that the server will eventually do the same.

    // Do validations against the canoncial username which is obtained on the
    // server via users.username._build_key_name()
    const canonicalUsername = username.replace(/\./g, "").toLowerCase();

    if (canonicalUsername.length < USERNAME_MIN_LENGTH) {
        return {
            result: false,
            reason: t`Too short.`,
        };
    }

    if (username.length > USERNAME_MAX_LENGTH) {
        return {
            result: false,
            reason: t`Too long.`,
        };
    }

    if (!/^[a-zA-Z]/.test(username)) {
        return {
            result: false,
            reason: t`Start with a letter.`,
        };
    }

    if (!/^[a-z0-9]+$/.test(canonicalUsername)) {
        return {
            result: false,
            reason: t`Letters and numbers only.`,
        };
    }

    return {result: true, reason: t`Looks good!`};
};

/**
 * Performs a check to see whether the provided username can be claimed by the
 * user (checks for validity and availability).
 *
 * The method performs client-side validations before going through server-side
 * validations.
 */
export const isUsernameAvailable = (
    gqlClient: ApolloGqlClient,
    username: string,
): Promise<{
    result: boolean;
    reason: string;
}> => {
    const isValid = isUsernameValid(username);

    if (!isValid.result) {
        return Promise.resolve(isValid);
    }

    // If the username passes all client-side validations, we pass it
    // to the server for validation (e.g. Mostly to ensure it's not
    // already claimed)
    return checkUsernameQuery(gqlClient, username)
        .then(({data}) => {
            const errorCode =
                data &&
                data.checkUsername &&
                data.checkUsername.error &&
                data.checkUsername.error.code;

            if (errorCode) {
                return {
                    result: false,
                    reason: getUsernameValidationString(errorCode),
                };
            }

            return {result: true, reason: t`Looks good!`};
        })
        .catch((error) => {
            // In the event that another error has occurred, we log it and
            // then inform the user something went wrong.
            Log.error(
                `An unexpected error occurred while validating a username`,
                Errors.Internal,
                {
                    cause: error,
                    sentryData: {
                        contexts: {extras: {username}},
                    },
                },
            );

            return {
                result: false,
                reason: t`Something went wrong.`,
            };
        });
};
